import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGlobalGroupsData } from "@/data/use-global-groups-data";
import { useNavigate } from "@tanstack/react-router";
import { ChangeEvent, useState } from "react";
import { toast } from "sonner";

type CreateGroupModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentGroupUuid?: string;
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 23);

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,21}[a-z0-9]$/;

const validateSlug = (slug: string): string | null => {
  if (slug.length < 3) return "Slug must be at least 3 characters.";
  if (slug.length > 23) return "Slug must be at most 23 characters.";
  if (!SLUG_REGEX.test(slug))
    return "Slug must be kebab-case (lowercase letters, numbers, and hyphens only).";
  return null;
};

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  open,
  onOpenChange,
  parentGroupUuid,
}) => {
  const { createGroup, isCreatingGroup } = useGlobalGroupsData();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  const bucketName = slug ? `nnr-ws-${slug}` : "";

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (!slugTouched) {
      const nextSlug = toSlug(newName);
      setSlug(nextSlug);
      setSlugError(validateSlug(nextSlug));
    }
  };

  const handleSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value;
    setSlug(newSlug);
    setSlugTouched(true);
    setSlugError(validateSlug(newSlug));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      return;
    }

    try {
      const result = await createGroup({
        name,
        slug,
        parentGroupUuid,
      });
      toast.success(`Group "${name}" created successfully.`);
      handleClose();
      void navigate({
        to: "/admin/groups/$groupUuid",
        params: { groupUuid: result.uuid },
      });
    } catch {
      toast.error("Failed to create group. Please try again.");
    }
  };

  const handleClose = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setSlugError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Group</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ws-name">Group Name</Label>
            <Input
              id="ws-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Acme Corp"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ws-slug">Slug</Label>
            <Input
              id="ws-slug"
              value={slug}
              onChange={handleSlugChange}
              placeholder="acme-corp"
            />
            {slugError && (
              <p className="text-destructive text-sm">{slugError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ws-bucket">
              Bucket Name (cannot be modified later)
            </Label>
            <Input
              id="ws-bucket"
              value={bucketName}
              readOnly
              disabled
              placeholder="nnr-ws-acme-corp"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCreatingGroup || !name || !!slugError}
            >
              {isCreatingGroup ? "Creating…" : "Create Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
